'use client'

import { sendTed } from '@/lib/api/financial/digital-account-api'
import { useMemo, useState } from 'react'
import { MdSwapHoriz } from 'react-icons/md'
import { toast } from 'react-toastify'
import PasswordField from './password-field'
import CurrencyInput from './currency-input'
import SearchableSelect from '@/components/searchable-select'
import { BRAZILIAN_BANKS } from '@/lib/constants/brazilian-banks'

export default function SendTedModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
    const [form, setForm] = useState({
        bankCode: '',
        agency: '',
        accountNumber: '',
        accountDigit: '',
        holderName: '',
        holderCpfCnpj: '',
        description: '',
    })
    const [amountCents, setAmountCents] = useState<number | null>(null)
    const [password, setPassword] = useState('')
    const [sending, setSending] = useState(false)

    const bankOptions = useMemo(
        () => BRAZILIAN_BANKS.map((b) => ({
            value: b.code,
            label: b.name,
            badge: b.code,
            description: `Código COMPE ${b.code}`,
        })),
        [],
    )

    const handleSend = async () => {
        if (!amountCents || amountCents <= 0) return toast.warning('Informe o valor')
        if (!form.bankCode || !form.agency || !form.accountNumber || !form.holderName || !form.holderCpfCnpj) {
            return toast.warning('Preencha os dados bancários')
        }
        if (!password) return toast.warning('Confirme sua senha')
        setSending(true)
        try {
            const res = await sendTed({
                bankCode: form.bankCode.trim(),
                agency: form.agency.trim(),
                accountNumber: form.accountNumber.trim(),
                accountDigit: form.accountDigit.trim() || undefined,
                holderName: form.holderName.trim(),
                holderCpfCnpj: form.holderCpfCnpj.replace(/\D/g, ''),
                amountCents,
                description: form.description.trim() || undefined,
                password,
            })
            if (res.success) {
                toast.success('Transferência enviada')
                onSuccess()
            } else {
                toast.error(res.error || 'Erro ao transferir')
            }
        } finally {
            setSending(false)
        }
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <h3 className="font-bold text-lg flex items-center gap-2 mb-4">
                    <MdSwapHoriz className="text-cyan-500" /> Transferência (TED)
                </h3>

                <div className="flex flex-col gap-1 mb-3">
                    <span className="text-sm font-medium text-base-content/80">Banco</span>
                    <SearchableSelect
                        options={bankOptions}
                        value={form.bankCode}
                        onChange={(value) => setForm({ ...form, bankCode: value })}
                        placeholder="Selecione o banco"
                        searchPlaceholder="Buscar por nome ou código..."
                        noResultsText="Nenhum banco encontrado"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">Agência</span>
                        <input className="input input-bordered input-sm" value={form.agency} onChange={e => setForm({ ...form, agency: e.target.value })} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">Conta + dígito</span>
                        <div className="flex gap-1">
                            <input className="input input-bordered input-sm flex-1" value={form.accountNumber} onChange={e => setForm({ ...form, accountNumber: e.target.value })} />
                            <input className="input input-bordered input-sm w-16" placeholder="DV" value={form.accountDigit} onChange={e => setForm({ ...form, accountDigit: e.target.value })} />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">Nome do favorecido</span>
                        <input className="input input-bordered input-sm" value={form.holderName} onChange={e => setForm({ ...form, holderName: e.target.value })} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">CPF/CNPJ</span>
                        <input className="input input-bordered input-sm" value={form.holderCpfCnpj} onChange={e => setForm({ ...form, holderCpfCnpj: e.target.value })} />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">Valor</span>
                        <CurrencyInput valueCents={amountCents} onChange={setAmountCents} disabled={sending} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">Descrição</span>
                        <input className="input input-bordered input-sm" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    </div>
                </div>

                <div className="mt-3">
                    <PasswordField value={password} onChange={setPassword} disabled={sending} />
                </div>

                <div className="modal-action">
                    <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={sending}>Cancelar</button>
                    <button className="btn btn-secondary btn-sm" onClick={handleSend} disabled={sending}>
                        {sending ? <span className="loading loading-spinner loading-xs" /> : 'Transferir'}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={() => !sending && onClose()} />
        </div>
    )
}
